import { Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import { AdminFrame } from '@/components/layout/AdminFrame';
import { AppShell } from '@/components/layout/AppShell';
import { RequireRole } from './guards';
import { HydrateFallback } from './HydrateFallback';
import { RouteError } from './RouteError';

/**
 * The page routes of the public site and the account area, built once: every page is a lazy route
 * module under `./routes` — one chunk each, exporting only `Component`.
 */
const pageRoutes: RouteObject[] = [
  { index: true, lazy: () => import('./routes/home') },

  // Catalogue
  { path: 'shop', lazy: () => import('./routes/shop') },
  { path: 'shop/:slug', lazy: () => import('./routes/product') },

  // Marketing
  { path: 'where-to-use', lazy: () => import('./routes/applications') },
  // the Monuments guide was removed (D-361): its old addresses lead to the page that lists the uses
  { path: 'where-to-use/monuments', lazy: () => import('./routes/monuments-moved') },
  { path: 'applications/monuments', lazy: () => import('./routes/monuments-moved') },
  { path: 'where-to-use/:slug', lazy: () => import('./routes/application-detail') },
  // the old address of the same pages (D-356): replaced by the new one, never a page of its own
  { path: 'applications', lazy: () => import('./routes/applications-moved') },
  { path: 'applications/:slug', lazy: () => import('./routes/applications-moved') },
  // the retired Hindi site (D-358): `/hi` and `/hi/…` are replaced by the English address of the page
  { path: 'hi/*', lazy: () => import('./routes/hindi-moved') },
  // the Privacy policy and the Terms of use were replaced by the Legal Disclaimer (D-372)
  { path: 'privacy', lazy: () => import('./routes/legal-moved') },
  { path: 'terms', lazy: () => import('./routes/legal-moved') },
  { path: 'visualizer', lazy: () => import('./routes/visualizer') },
  { path: 'gallery', lazy: () => import('./routes/gallery') },
  { path: 'contact-us', lazy: () => import('./routes/contact') },

  // Auth
  { path: 'login', lazy: () => import('./routes/login') },
  { path: 'login/two-factor', lazy: () => import('./routes/login-two-factor') },
  // Signing in is for staff and only with Google (D-375): the pages of the customer sign-in are gone and their old
  // addresses lead to the one login page; an old account address leads to the back-office (which asks for the login)
  { path: 'register', lazy: () => import('./routes/login-moved') },
  { path: 'forgot-password', lazy: () => import('./routes/login-moved') },
  { path: 'reset-password', lazy: () => import('./routes/login-moved') },
  { path: 'verify-email', lazy: () => import('./routes/login-moved') },
  { path: 'confirm-email-change', lazy: () => import('./routes/login-moved') },
  { path: 'restore-account', lazy: () => import('./routes/login-moved') },
  { path: 'account', lazy: () => import('./routes/account-moved') },
  { path: 'account/*', lazy: () => import('./routes/account-moved') },
  { path: 'contact-us/confirm', lazy: () => import('./routes/contact-confirm') },

  // Legal
  { path: 'legal-disclaimer', lazy: () => import('./routes/legal-disclaimer') },

  // Style guide: development only (`import.meta.env.DEV` is a build-time constant, so the route
  // and its chunk vanish from production builds unless VITE_ENABLE_DESIGN_PAGE=true).
  ...(import.meta.env.DEV || import.meta.env.VITE_ENABLE_DESIGN_PAGE === 'true'
    ? [{ path: 'design', lazy: () => import('./routes/design') } satisfies RouteObject]
    : []),
  // Feature previews (`/preview/home-story` …): development only, never in a build.
  ...(import.meta.env.DEV
    ? [
        {
          path: 'preview/:name',
          lazy: () => import('./routes/preview'),
        } satisfies RouteObject,
      ]
    : []),

  { path: '*', lazy: () => import('./routes/not-found') },
];

/**
 * The route table (ARCHITECTURE §2.1). Kept separate from `createBrowserRouter` (see router.tsx) so
 * the same table drives `createStaticHandler` when the public routes are pre-rendered.
 *
 *   /                        shell (header · main · footer) — errors inside keep the shell
 *   └─ (pathless)            the pages
 *       ├─ public pages …
 *       ├─ hi/*              the retired Hindi addresses: replaced by the English page (D-358)
 *       └─ *                 404
 *   /admin                   back-office frame (no public chrome)
 *   └─ RequireRole ADMIN     /admin/*                           (ADMIN + SUPER_ADMIN)
 *       └─ RequireRole SUPER_ADMIN   /admin/super/*             (SUPER_ADMIN only)
 *
 * Guards are layout routes around the lazy children (what renders is decided by the session state).
 * They are UX only — the API enforces authentication and roles on every request.
 */
export const routeObjects: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteError standalone />,
    hydrateFallbackElement: <HydrateFallback />,
    children: [
      {
        // Pathless wrapper: a failing page renders <RouteError /> inside the shell's <main>.
        errorElement: <RouteError />,
        children: pageRoutes,
      },
    ],
  },

  // Back-office: its own frame (no marketing header/footer), English only. ADMIN and SUPER_ADMIN
  // may enter; the Super Admin sub-tree is SUPER_ADMIN only. Static `/admin/*` outranks the shell's
  // `*` catch-all, so unknown admin URLs get the admin 404 rather than the public one.
  {
    path: '/admin',
    element: <AdminFrame />,
    errorElement: <RouteError standalone />,
    hydrateFallbackElement: <HydrateFallback />,
    children: [
      {
        element: <RequireRole minRole="ADMIN" landmark />,
        children: [
          {
            lazy: () => import('./routes/admin/layout'),
            children: [
              {
                // A failing page renders <RouteError /> inside the admin layout's <main>.
                errorElement: <RouteError />,
                children: [
                  { index: true, lazy: () => import('./routes/admin/dashboard') },
                  { path: 'products', lazy: () => import('./routes/admin/products') },
                  { path: 'products/new', lazy: () => import('./routes/admin/product-new') },
                  { path: 'products/:id', lazy: () => import('./routes/admin/product-edit') },
                  { path: 'categories', lazy: () => import('./routes/admin/categories') },
                  { path: 'attributes', lazy: () => import('./routes/admin/attributes') },
                  { path: 'enquiries', lazy: () => import('./routes/admin/enquiries') },
                  { path: 'enquiries/:id', lazy: () => import('./routes/admin/enquiry-detail') },
                  { path: 'content', lazy: () => import('./routes/admin/content') },
                  { path: 'media', lazy: () => import('./routes/admin/media') },
                  { path: 'gallery', lazy: () => import('./routes/admin/gallery') },
                  {
                    path: 'super',
                    element: <RequireRole minRole="SUPER_ADMIN" />,
                    children: [
                      { index: true, element: <Navigate to="users" replace /> },
                      { path: 'users', lazy: () => import('./routes/admin/super/users') },
                      { path: 'audit', lazy: () => import('./routes/admin/super/audit') },
                      { path: 'settings', lazy: () => import('./routes/admin/super/settings') },
                    ],
                  },
                  { path: '*', lazy: () => import('./routes/admin/not-found') },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
